import { useState, useCallback, useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';

interface UseEnhancedTextSelectionProps {
  onTextSelect?: (text: string) => void;
  enableSmartHighlighting?: boolean;
  enableAccessibility?: boolean;
}

export const useEnhancedTextSelection = ({ 
  onTextSelect, 
  enableSmartHighlighting = true,
  enableAccessibility = true 
}: UseEnhancedTextSelectionProps = {}) => {
  const [selectedText, setSelectedText] = useState<string>('');
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionMetadata, setSelectionMetadata] = useState<{
    wordCount: number;
    charCount: number;
    pageNumber?: number;
    selectionRect?: DOMRect;
  }>({ wordCount: 0, charCount: 0 });
  const [showOverlay, setShowOverlay] = useState(false);
  const selectionTimeout = useRef<NodeJS.Timeout>();
  const { toast } = useToast();

  const updateSelectionMetadata = useCallback((text: string, selection?: Selection) => {
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    const metadata = {
      wordCount: words.length,
      charCount: text.length,
      pageNumber: undefined as number | undefined,
      selectionRect: undefined as DOMRect | undefined
    };

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      metadata.selectionRect = range.getBoundingClientRect();
      
      // Try to find page number
      const pageElement = range.commonAncestorContainer.parentElement?.closest('[data-page-number]');
      if (pageElement) {
        metadata.pageNumber = parseInt(pageElement.getAttribute('data-page-number') || '1');
      }
    }

    setSelectionMetadata(metadata);
  }, []);

  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString().trim() || '';
    
    if (text !== selectedText) {
      setSelectedText(text);
      updateSelectionMetadata(text, selection || undefined);
      
      if (text) {
        setShowOverlay(true);
        if (onTextSelect) {
          onTextSelect(text);
        }
      } else {
        setShowOverlay(false);
      }
    }
  }, [selectedText, onTextSelect, updateSelectionMetadata]);

  const copySelectedText = useCallback(async (format: 'plain' | 'rich' = 'plain') => {
    if (!selectedText) return false;

    try {
      let textToCopy = selectedText;
      
      // Add metadata for rich format
      if (format === 'rich' && selectionMetadata.pageNumber) {
        textToCopy = `"${selectedText}"\n\n[Source: Page ${selectionMetadata.pageNumber}]`;
      }

      await navigator.clipboard.writeText(textToCopy);
      
      toast({
        title: "Text Copied",
        description: `Copied ${selectionMetadata.wordCount} words (${selectionMetadata.charCount} chars)`,
        duration: 2000
      });
      return true;
    } catch (error) {
      console.error('Failed to copy text:', error);
      toast({
        title: "Copy Failed",
        description: "Could not copy text to clipboard",
        variant: "destructive",
        duration: 3000
      });
      return false;
    }
  }, [selectedText, selectionMetadata, toast]);

  const extractSelectedText = useCallback(() => {
    if (!selectedText) return null;

    return {
      text: selectedText,
      metadata: selectionMetadata,
      timestamp: new Date().toISOString(),
      formatted: {
        plain: selectedText,
        withMetadata: selectionMetadata.pageNumber 
          ? `"${selectedText}"\n\n[Source: Page ${selectionMetadata.pageNumber}, ${selectionMetadata.wordCount} words]`
          : selectedText
      }
    };
  }, [selectedText, selectionMetadata]);

  const selectAllText = useCallback(() => {
    const textLayer = document.querySelector('.react-pdf__Page__textContent');
    if (textLayer) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(textLayer);
      selection?.removeAllRanges();
      selection?.addRange(range);
      
      toast({
        title: "All Text Selected",
        description: "Use Ctrl+C to copy or click Copy button",
        duration: 2000
      });
    }
  }, [toast]);

  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setSelectedText('');
    setShowOverlay(false);
    setSelectionMetadata({ wordCount: 0, charCount: 0 });
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'a':
            const target = event.target as Element;
            if (target?.closest('.pdf-page-container')) {
              event.preventDefault();
              selectAllText();
            }
            break;
          case 'c':
            if (selectedText) {
              copySelectedText();
            }
            break;
          case 'f':
            // Let parent component handle search
            break;
        }
      } else if (event.key === 'Escape') {
        clearSelection();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange, selectAllText, copySelectedText, selectedText]);

  // Track selection state
  useEffect(() => {
    const handleMouseDown = () => setIsSelecting(true);
    const handleMouseUp = () => {
      setTimeout(() => setIsSelecting(false), 100);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Smart highlighting effects
  useEffect(() => {
    if (!enableSmartHighlighting) return;

    const handleMouseMove = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target?.closest('.react-pdf__Page__textContent')) {
        // Add hover effects for text elements
        const textSpan = target.closest('span');
        if (textSpan) {
          textSpan.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
          textSpan.style.transition = 'background-color 0.15s ease';
        }
      }
    };

    const handleMouseLeave = (event: MouseEvent) => {
      const target = event.target as Element;
      const textSpan = target.closest('span');
      if (textSpan && !window.getSelection()?.toString()) {
        textSpan.style.backgroundColor = '';
      }
    };

    document.addEventListener('mouseover', handleMouseMove);
    document.addEventListener('mouseout', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseover', handleMouseMove);
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [enableSmartHighlighting]);

  // Auto-hide overlay
  useEffect(() => {
    if (showOverlay) {
      clearTimeout(selectionTimeout.current);
      selectionTimeout.current = setTimeout(() => {
        if (!isSelecting) {
          setShowOverlay(false);
        }
      }, 5000);
    }

    return () => {
      clearTimeout(selectionTimeout.current);
    };
  }, [showOverlay, isSelecting]);

  return {
    selectedText,
    isSelecting,
    selectionMetadata,
    showOverlay,
    copySelectedText,
    extractSelectedText,
    selectAllText,
    clearSelection,
    hasSelection: selectedText.length > 0
  };
};